import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Drawer, Spin, Table } from "antd";
import dayjs from "dayjs";
import HighchartsReact from "highcharts-react-official";
import { getUsageGeoCountryUsers, getUsageGeoMap } from "../../../apis/analyticsUsageApi";
import { barChartOptions, formatNumber, THEME } from "./chartHelpers";
import { buildUserColumns } from "./WidgetDetailBreakdown";
import { analyticsDrawerOverlayProps } from "./analyticsModalUtils";
import { ANALYTICS_DATE_PICKER_CLASS } from "./analyticsTheme";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";
import AnalyticsPanelHead from "./AnalyticsPanelHead";
import WorldUserMap from "./WorldUserMap";

const { RangePicker } = DatePicker;
const MAX_RANGE_DAYS = 90;
const defaultRange = () => [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")];

const drawerTitle = (selection) => {
  if (!selection) return "Users";
  if (selection.type === "cluster") {
    return `${selection.count} user(s) in this area`;
  }
  if (selection.type === "country") {
    return selection.city
      ? `Users in ${selection.city}, ${selection.country}`
      : `Users in ${selection.country}`;
  }
  const m = selection.marker;
  if (m?.user?.id) {
    const name = [m.user.first_name, m.user.last_name].filter(Boolean).join(" ");
    return name || m.user.email || `User #${m.user.id}`;
  }
  return m?.city || m?.country || "User details";
};

const AnalyticsGeoMap = () => {
  const [dateRange, setDateRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [drawerUsers, setDrawerUsers] = useState([]);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [selection, setSelection] = useState(null);

  const params = useMemo(
    () => ({
      from: dateRange[0].format("YYYY-MM-DD"),
      to: dateRange[1].format("YYYY-MM-DD"),
    }),
    [dateRange]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUsageGeoMap(params);
      setData(res?.result || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to load geo data");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const openUsers = useCallback((users, count, meta = {}) => {
    setSelection({ type: meta.type || "cluster", count: count ?? users.length, ...meta });
    setDrawerUsers(users);
    setDrawerLoading(false);
  }, []);

  const handleClusterSelect = useCallback(
    (users, count) => openUsers(users, count, { type: "cluster" }),
    [openUsers]
  );

  const handleMarkerSelect = useCallback(
    (marker) => openUsers([marker], 1, { type: "marker", marker }),
    [openUsers]
  );

  const openCountry = async (country, city) => {
    setSelection({ type: "country", country, city: city || null });
    setDrawerLoading(true);
    setDrawerUsers([]);
    try {
      const res = await getUsageGeoCountryUsers({
        ...params,
        country,
        ...(city ? { city } : {}),
      });
      setDrawerUsers(res?.result?.users || []);
    } finally {
      setDrawerLoading(false);
    }
  };

  const countryBar = useMemo(() => {
    const items = data?.countries || [];
    return barChartOptions({
      categories: items.slice(0, 12).map((c) => c.country),
      data: items.slice(0, 12).map((c) => c.users),
      color: THEME.success,
    });
  }, [data]);

  const countryColumns = [
    { title: "Country", dataIndex: "country", key: "country" },
    { title: "Users", dataIndex: "users", key: "users", render: formatNumber },
    { title: "Events", dataIndex: "events", key: "events", render: formatNumber },
    {
      title: "",
      key: "action",
      width: 100,
      render: (_, row) => (
        <button
          type="button"
          className="analytics-view-more"
          onClick={() => openCountry(row.country)}
        >
          Users <i className="fa-solid fa-arrow-right" />
        </button>
      ),
    },
  ];

  const markers = data?.user_markers || [];

  return (
    <div className="analytics-dashboard analytics-geo-map">
      <header className="analytics-dashboard__header">
        <div>
          <h1>
            Users Map
            <AnalyticsHelpIcon helpKey="geo-page" />
          </h1>
          <p>
            World map with clustered users by location — zoom in/out, click a cluster count to see users
          </p>
        </div>
        <div className="analytics-dashboard__controls">
          <RangePicker
            popupClassName={ANALYTICS_DATE_PICKER_CLASS}
            value={dateRange}
            onChange={(dates) => {
              if (!dates?.[0] || !dates?.[1]) return;
              if (dates[1].diff(dates[0], "day") > MAX_RANGE_DAYS) {
                setError(`Max range is ${MAX_RANGE_DAYS} days`);
                return;
              }
              setDateRange(dates);
            }}
            allowClear={false}
            disabledDate={(c) => c && c > dayjs().endOf("day")}
          />
          <Button type="primary" onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error && <Alert type="error" message={error} closable onClose={() => setError(null)} showIcon />}

      {loading && !data ? (
        <div className="analytics-dashboard__loading">
          <Spin size="large" tip="Loading map data..." />
        </div>
      ) : (
        <>
          <div className="analytics-kpi-grid analytics-kpi-grid--user">
            <div className="analytics-kpi-card">
              <span className="analytics-kpi-card__label">Countries</span>
              <div className="analytics-kpi-card__value">{formatNumber(data?.totals?.countries)}</div>
            </div>
            <div className="analytics-kpi-card">
              <span className="analytics-kpi-card__label">Cities</span>
              <div className="analytics-kpi-card__value">{formatNumber(data?.totals?.cities)}</div>
            </div>
            <div className="analytics-kpi-card">
              <span className="analytics-kpi-card__label">On map</span>
              <div className="analytics-kpi-card__value">{formatNumber(data?.totals?.mapped_users)}</div>
            </div>
          </div>

          <div className="analytics-panel analytics-panel--map">
            <AnalyticsPanelHead
              title="World map"
              helpKey="geo-world-map"
              subtitle={`${formatNumber(markers.length)} users with coordinates · click cluster or pin for details`}
            />
            {markers.length ? (
              <WorldUserMap
                markers={markers}
                onSelectUsers={handleClusterSelect}
                onSelectUser={handleMarkerSelect}
              />
            ) : (
              <p className="analytics-empty analytics-empty--map">
                No geo coordinates in this period. Ensure PostHog geoIP is enabled on inv-fe events.
              </p>
            )}
          </div>

          <div className="analytics-grid analytics-grid--2">
            <div className="analytics-panel">
              <AnalyticsPanelHead title="Top countries" helpKey="geo-top-countries" />
              <HighchartsReact options={countryBar} />
            </div>
            <div className="analytics-panel">
              <AnalyticsPanelHead title="Countries" helpKey="geo-countries-table" />
              <Table
                size="small"
                className="analytics-table"
                columns={countryColumns}
                dataSource={(data?.countries || []).map((r, i) => ({ ...r, key: i }))}
                pagination={{ pageSize: 8, size: "small" }}
                onRow={(row) => ({
                  onClick: () => openCountry(row.country),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </div>
        </>
      )}

      <Drawer
        title={drawerTitle(selection)}
        open={Boolean(selection)}
        onClose={() => {
          setSelection(null);
          setDrawerUsers([]);
        }}
        width={720}
        className="analytics-detail-drawer"
        {...analyticsDrawerOverlayProps}
      >
        {drawerLoading ? (
          <Spin tip="Loading users..." />
        ) : (
          <Table
            size="small"
            className="analytics-table"
            columns={buildUserColumns()}
            dataSource={drawerUsers.map((r, i) => ({ ...r, key: r.distinct_id || i }))}
            pagination={{ pageSize: 15, size: "small" }}
            locale={{ emptyText: "No users for this selection" }}
          />
        )}
      </Drawer>
    </div>
  );
};

export default AnalyticsGeoMap;
